import React, { forwardRef, useLayoutEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigationMeta } from './NavigationContext';

const EASE = [0.22, 0.6, 0.2, 1];

const variants = {
  slide: {
    initial: (dir) => ({ opacity: 0, x: dir * 80 }),
    animate: { opacity: 1, x: 0 },
    exit: (dir) => ({ opacity: 0, x: dir * -60 }),
  },
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  'zoom-in': {
    initial: { opacity: 0, scale: 0.12, filter: 'blur(14px)' },
    animate: { opacity: 1, scale: 1, filter: 'blur(0px)' },
    exit: { opacity: 0, scale: 1.35, filter: 'blur(14px)' },
  },
  'zoom-out': {
    initial: { opacity: 0, scale: 1.35, filter: 'blur(14px)' },
    animate: { opacity: 1, scale: 1, filter: 'blur(0px)' },
    exit: { opacity: 0, scale: 0.12, filter: 'blur(14px)' },
  },
};

const DURATIONS = {
  slide: 0.55,
  fade: 0.45,
  'zoom-in': 0.85,
  'zoom-out': 0.85,
};

// AnimatePresence mode="popLayout" attaches a ref to its immediate child
// (PopChild → cloneElement with ref). PageTransition has to forward that ref
// onto the inner motion.div, otherwise React warns and popLayout layout
// measurements break — which made the slide/fade exits feel snappy/missing.
const PageTransition = forwardRef(({ children, type: typeProp }, ref) => {
  const { transitionType, direction } = useNavigationMeta();
  const type = typeProp || transitionType || 'fade';
  const v = variants[type] || variants.fade;
  const duration = DURATIONS[type] || 0.5;

  // Reset scroll to the top of every newly-mounted page so a project page
  // never inherits the home page's scroll position. Runs synchronously
  // before paint so the new page never flashes at a non-zero scroll.
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  // Only zoom transitions use the click-point origin. Slide/fade always
  // animate from screen centre so a previous click doesn't make the page
  // shoot off at a weird angle on the next nav.
  const isZoom = type === 'zoom-in' || type === 'zoom-out';
  const transformOrigin = isZoom ? 'var(--zoom-origin, 50% 50%)' : '50% 50%';

  return (
    <motion.div
      ref={ref}
      className="page-transition"
      custom={direction}
      variants={v}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration, ease: EASE }}
      style={{
        transformOrigin,
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        willChange: 'transform, opacity',
      }}
    >
      {children}
    </motion.div>
  );
});

PageTransition.displayName = 'PageTransition';

export default PageTransition;
